import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import polygonClipping from 'polygon-clipping';
import { MultiPolygon } from 'ol/geom';
import { getArea } from 'ol/sphere';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Style, Stroke } from 'ol/style';
import { Button } from 'react-bootstrap';
import { LuTable } from 'react-icons/lu';

import { formatNumber } from '@utils/helpers';
import { formatArea } from '@utils/measurementSystem';
import { deleteLayerByCustomId } from '@utils/map/helpers';
import { useProject } from '@contexts/Project.context';
import { DSP_DETECTIONS_SIDEBAR_ID } from '../../sidebars/DspDetectionsPerPlotSidebar';
import InfoTooltip from '@components/infoTooltip/InfoTooltip';

const BOUNDARY_LAYER_ID = 'dspUnionBoundaryLayer';

const boundaryStyle = new Style({
	stroke: new Stroke({
		color: 'rgba(255, 165, 0, 0.9)',
		width: 2.5,
		lineDash: [8, 6],
	}),
});

/**
 * Computes aggregate statistics for the DSP details panel.
 * "Mean hectare equivalent" = total hectare equivalent / number of DSP plots.
 * Per-class means use the same divisor (total plots), so they sum to the "all classes" mean.
 */
const computeStatistics = dspFeatures => {
	const totalDetectionCount = dspFeatures.reduce(
		(sum, f) => sum + (f.get('totalDetectsCount') || 0),
		0
	);

	const classHectareMap = {};
	let totalHectareEquivalent = 0;

	dspFeatures.forEach(f => {
		const classes = f.get('detectionClasses') || [];
		classes.forEach(c => {
			const he = c.hectare_equivalent || 0;
			totalHectareEquivalent += he;
			const name = c.className || 'Unknown';
			if (!classHectareMap[name]) {
				classHectareMap[name] = 0;
			}
			classHectareMap[name] += he;
		});
	});

	const totalFootprintArea = dspFeatures.reduce((sum, f) => sum + (f.get('area') || 0), 0);

	const totalImages = dspFeatures.length;

	const meanHectareEquivalent = totalImages > 0 ? totalHectareEquivalent / totalImages : 0;

	const perClassMeanHectareEquivalent = Object.entries(classHectareMap)
		.map(([className, total]) => {
			const mean = totalImages > 0 ? total / totalImages : 0;
			return { className, meanHectareEquivalent: Number.isFinite(mean) ? mean : 0 };
		})
		.sort((a, b) => a.className.localeCompare(b.className));

	return {
		totalDetectionCount,
		totalHectareEquivalent,
		meanHectareEquivalent,
		perClassMeanHectareEquivalent,
		totalFootprintArea,
		totalImages,
	};
};

const computeUnion = dspFeatures => {
	if (!dspFeatures.length) {
		return { totalUniqueArea: null, unionGeom: null, unionFailed: false };
	}

	try {
		const polygons = dspFeatures.map(f => f.getGeometry().getCoordinates());
		const unionResult = polygonClipping.union(...polygons);
		const unionGeom = new MultiPolygon(unionResult);
		const totalUniqueArea = getArea(unionGeom);
		return { totalUniqueArea, unionGeom, unionFailed: false };
	} catch (err) {
		console.warn('Polygon union failed:', err);
		return { totalUniqueArea: null, unionGeom: null, unionFailed: true };
	}
};

const extractPerClassPerPlot = dspData => {
	if (!dspData?.plot_data) return [];

	return Object.entries(dspData.plot_data)
		.filter(([_, item]) => item?.metadata?.plot_polygon)
		.map(([key, item]) => ({
			plotFilename: item.metadata.filename,
			classes: Object.entries(item.detections || {}).map(([className, values]) => ({
				className,
				count: values.count,
				hectareEquivalent: values.hectare_equivalent,
			})),
		}));
};

const DspDetailsPanel = ({ dspFeatures, mapObject, isOpen, perClassPerPlot = [] }) => {
	const boundaryLayerRef = useRef(null);
	const { setActiveSidebar } = useProject();

	const stats = useMemo(() => computeStatistics(dspFeatures || []), [dspFeatures]);

	const { totalUniqueArea, unionGeom, unionFailed } = useMemo(
		() => computeUnion(dspFeatures || []),
		[dspFeatures]
	);

	// Render/remove boundary layer based on panel visibility
	useEffect(() => {
		if (!mapObject || !unionGeom || !isOpen) {
			// Remove boundary if panel is collapsed
			if (boundaryLayerRef.current) {
				deleteLayerByCustomId(mapObject, BOUNDARY_LAYER_ID);
				boundaryLayerRef.current = null;
			}
			return;
		}

		// Create boundary layer
		const boundaryFeature = new Feature({ geometry: unionGeom });
		boundaryFeature.setStyle(boundaryStyle);

		const boundaryLayer = new VectorLayer({
			source: new VectorSource({ features: [boundaryFeature] }),
			zIndex: 15,
			properties: { customLayerId: BOUNDARY_LAYER_ID },
		});

		mapObject.addLayer(boundaryLayer);
		boundaryLayerRef.current = boundaryLayer;

		return () => {
			deleteLayerByCustomId(mapObject, BOUNDARY_LAYER_ID);
			boundaryLayerRef.current = null;
		};
	}, [mapObject, unionGeom, isOpen]);

	const showDetectionsPerPlot = () => {
		setActiveSidebar({ sidebarId: DSP_DETECTIONS_SIDEBAR_ID, parentId: 'layerView' });
	};

	const [footprintAreaFormatted, footprintAreaUnit] = formatArea(stats.totalFootprintArea);

	const uniqueAreaDisplay = totalUniqueArea != null ? formatArea(totalUniqueArea) : null;

	return (
		<PanelWrapper>
			<LabelDetails>
				<p>
					<Name>
						<InfoTooltip
							tooltipContainerId="tooltip-sidebar-root"
							placement="right"
							content="The total number of individual trees or stems identified across all sample plots."
							showIcon={false}>
							Total Detections
						</InfoTooltip>
					</Name>
					<span>{formatNumber(stats.totalDetectionCount)}</span>
				</p>
				<p>
					<Name>
						<InfoTooltip
							tooltipContainerId="tooltip-sidebar-root"
							placement="right"
							content="The total number of individual sample plots (drone images) analyzed in this project."
							showIcon={false}>
							Total Images
						</InfoTooltip>
					</Name>
					<span>{stats.totalImages}</span>
				</p>
				<p>
					<Name>
						<InfoTooltip
							tooltipContainerId="tooltip-sidebar-root"
							placement="right"
							content="The gross ground area covered by all sample plots combined, including overlapping regions."
							showIcon={false}>
							Total Footprint Area
						</InfoTooltip>
					</Name>
					<span>
						{footprintAreaFormatted} <Unit>{footprintAreaUnit}</Unit>
					</span>
				</p>
				{uniqueAreaDisplay && (
					<p>
						<Name>
							<InfoTooltip
								tooltipContainerId="tooltip-sidebar-root"
								placement="right"
								content="The actual, de-duplicated ground area mapped, calculated by removing overlapping regions."
								showIcon={false}>
								Unique Coverage
							</InfoTooltip>
						</Name>
						<span>
							{uniqueAreaDisplay[0]} <Unit>{uniqueAreaDisplay[1]}</Unit>
						</span>
					</p>
				)}
				<p>
					<Name>
						<InfoTooltip
							tooltipContainerId="tooltip-sidebar-root"
							placement="right"
							content="The sum of the extrapolated density across all plots, scaling local detections up to a full hectare (10,000 m²)."
							showIcon={false}>
							Hectare Equivalent
						</InfoTooltip>
					</Name>
					<span>
						{formatNumber(stats.totalHectareEquivalent, 2)} <Unit>/ ha</Unit>
					</span>
				</p>
				<p>
					<Name>
						<InfoTooltip
							tooltipContainerId="tooltip-sidebar-root"
							placement="right"
							content="The average extrapolated tree density per hectare across all sample plots."
							showIcon={false}>
							DSP mean ha eq all
						</InfoTooltip>
					</Name>
					<span>
						{formatNumber(stats.meanHectareEquivalent, 2)} <Unit>/ ha</Unit>
					</span>
				</p>
				{stats.perClassMeanHectareEquivalent.map(cls => (
					<p key={cls.className}>
						<Name>
							<InfoTooltip
								tooltipContainerId="tooltip-sidebar-root"
								placement="right"
								content={`The average extrapolated density per hectare specifically for ${cls.className}.`}
								showIcon={false}>
								DSP mean ha eq {cls.className}
							</InfoTooltip>
						</Name>
						<span>
							{formatNumber(cls.meanHectareEquivalent, 2)} <Unit>/ ha</Unit>
						</span>
					</p>
				))}
				{unionFailed && (
					<WarningText>
						⚠ Could not compute unique area — showing total footprint area instead.
					</WarningText>
				)}
			</LabelDetails>

			{perClassPerPlot.length > 0 && (
				<ButtonWrapper>
					<Button
						variant="dark"
						size="sm"
						className="py-1 px-2 d-flex align-items-center justify-content-center gap-2 w-100"
						aria-label="View detections per plot"
						onClick={showDetectionsPerPlot}>
						<span>
							<LuTable /> Detections per Plot
						</span>
					</Button>
				</ButtonWrapper>
			)}
		</PanelWrapper>
	);
};

export default DspDetailsPanel;

// Exported for use in SingleImageFootprintsLayer and testing
export { computeStatistics, computeUnion, extractPerClassPerPlot };

// Styled components
const PanelWrapper = styled.div`
	margin: 5px 0 10px;
`;

const LabelDetails = styled.div`
	font-size: 0.9em;
	font-weight: 400;
	p {
		display: grid;
		grid-template-columns: auto 110px;
		row-gap: 6px;
		justify-content: space-between;
		margin: 0;
	}
`;

const Unit = styled.span`
	text-wrap: nowrap;
`;

const Name = styled.span`
	text-transform: capitalize;
	color: ${({ theme }) => theme.colors.textAccent};
	display: block;
	cursor: help;
`;

const WarningText = styled.p`
	font-size: 0.8em;
	color: ${({ theme }) => theme.colors.warning || '#f0ad4e'};
	margin-top: 4px;
`;

const ButtonWrapper = styled.div`
	margin-top: 14px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
`;
